<script setup lang="ts">
import { computed } from "vue"

interface Props {
  appendIcon?: string
  autofocus?: boolean
  clearable?: boolean
  disabled?: boolean
  errorMessages?: string[] | string
  hideDetails?: boolean | string
  hint?: string
  label?: string
  max?: number | string
  min?: number | string
  persistentHint?: boolean
  placeholder?: string
  required?: boolean
  singleLine?: boolean
  type?: string
  modelValue?: any
}

const props = withDefaults(defineProps<Props>(), {
  autofocus: false,
  clearable: false,
  errorMessages: () => [],
  hideDetails: false,
  persistentHint: false,
  required: false,
  singleLine: false,
  type: "text",
})

const emit = defineEmits<{
  blur: [event: Event]
  change: [event: Event]
  click: [event: Event]
  input: [value: any]
  keyup: [event: KeyboardEvent]
  "update:modelValue": [value: any]
}>()

const labelProcessed = computed<string | undefined>(() => {
  if (props.required) {
    return props.label + " *"
  }
  return props.label
})

const localValue = computed<any>({
  get() {
    return props.modelValue
  },
  set(v: any) {
    emit("update:modelValue", v)
  },
})
</script>

<template>
  <v-text-field
    v-model="localValue"
    :append-icon="appendIcon"
    :autofocus="autofocus"
    :clearable="clearable"
    :disabled="disabled"
    :error-messages="errorMessages"
    :hide-details="hideDetails"
    :hint="hint"
    :label="labelProcessed"
    :max="max"
    :min="min"
    variant="outlined"
    :persistent-hint="persistentHint"
    :placeholder="placeholder"
    :required="required"
    :single-line="singleLine"
    :type="type"
    @blur="emit('blur', $event)"
    @change="emit('change', $event)"
    @click="emit('click', $event)"
    @update:model-value="emit('input', $event)"
    @keyup="emit('keyup', $event)"
  >
    <template #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template #append-inner>
      <slot name="append-inner" />
    </template>
  </v-text-field>
</template>
